import { useEffect, useState } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Download, FileSpreadsheet, Mail, UserPlus, Upload, FileText, Trash2, Eye, Send, MapPin, RefreshCw, Pin, Filter, X, FolderPlus, FolderOpen, Settings, Percent, Pencil } from 'lucide-react';
import * as XLSX from 'xlsx';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'booked', label: 'Booked' },
  { value: 'en_route_to_pickup', label: 'En Route to Pickup' },
  { value: 'at_pickup', label: 'At Pickup' },
  { value: 'picked_up', label: 'Picked Up' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'en_route_to_delivery', label: 'En Route to Delivery' },
  { value: 'at_delivery', label: 'At Delivery' },
  { value: 'dropped_off', label: 'Dropped Off' },
  { value: 'completed', label: 'Completed' },
];

export default function Loads({ user }) {
  const [loads, setLoads] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [shippers, setShippers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [selectedLoadForDocs, setSelectedLoadForDocs] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState('');
  const [filterCarrier, setFilterCarrier] = useState('all');
  const [filterBroker, setFilterBroker] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [commission, setCommission] = useState({ dispatch_fee_percent: 10, factoring_percent: 4 });
  const [showCommission, setShowCommission] = useState(false);
  const [commissionForm, setCommissionForm] = useState({ dispatch_fee_percent: '10', factoring_percent: '4' });
  const [sendDocModal, setSendDocModal] = useState(null);
  const [sendDocEmail, setSendDocEmail] = useState('');
  const [sendDocType, setSendDocType] = useState('factoring');
  const [editingLoad, setEditingLoad] = useState(null);
  const [driverFormData, setDriverFormData] = useState({
    name: '', phone: '', email: '', carrier_id: '', truck_number: '', trailer_number: ''
  });
  const [formData, setFormData] = useState({
    load_number: '', shipper_id: '', carrier_id: '', driver_id: '',
    equipment_type: '', pickup_location: '', pickup_city: '', pickup_state: '',
    pickup_date: '', pickup_time: '', delivery_location: '', delivery_city: '',
    delivery_state: '', delivery_date: '', delivery_time: '', commodity: '',
    weight: 0, temperature: '', rate_to_shipper: 0, rate_to_carrier: 0,
    fuel_surcharge: 0, accessorials: 0, special_instructions: '', cc_emails: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh every 30 seconds when enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadData = () => {
    Promise.all([
      api.get('/loads'),
      api.get('/carriers'),
      api.get('/shippers'),
      api.get('/drivers'),
      api.get('/org/commission-settings').catch(() => ({ data: { dispatch_fee_percent: 10, factoring_percent: 4 } }))
    ]).then(([loadsRes, carriersRes, shippersRes, driversRes, commRes]) => {
      setLoads(loadsRes.data);
      setCarriers(carriersRes.data);
      setShippers(shippersRes.data);
      setDrivers(driversRes.data);
      setCommission(commRes.data);
      setCommissionForm({
        dispatch_fee_percent: String(commRes.data.dispatch_fee_percent),
        factoring_percent: String(commRes.data.factoring_percent)
      });
    }).catch(() => toast.error('Failed to load data'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const loadNumber = `LOAD-${Date.now()}`;
    const ccList = formData.cc_emails ? formData.cc_emails.split(',').map(e => e.trim()).filter(Boolean) : [];
    api.post('/loads', {...formData, load_number: loadNumber, cc_emails: ccList})
      .then(() => {
        toast.success('Load booked successfully!', {
          description: 'Email notifications sent to carrier & driver'
        });
        setShowForm(false);
        loadData();
        resetForm();
      })
      .catch(() => toast.error('Failed to book load'));
  };

  const handleDriverSubmit = (e) => {
    e.preventDefault();
    api.post('/drivers', driverFormData)
      .then(() => {
        toast.success('Driver added successfully');
        setShowDriverForm(false);
        loadData();
        setDriverFormData({ name: '', phone: '', email: '', carrier_id: '', truck_number: '', trailer_number: '' });
      })
      .catch(() => toast.error('Failed to add driver'));
  };

  const startEditLoad = (load) => {
    setFormData({
      load_number: load.load_number || '',
      shipper_id: load.shipper_id || '',
      carrier_id: load.carrier_id || '',
      driver_id: load.driver_id || '',
      equipment_type: load.equipment_type || '',
      pickup_location: load.pickup_location || '',
      pickup_city: load.pickup_city || '',
      pickup_state: load.pickup_state || '',
      pickup_date: load.pickup_date || '',
      pickup_time: load.pickup_time || '',
      delivery_location: load.delivery_location || '',
      delivery_city: load.delivery_city || '',
      delivery_state: load.delivery_state || '',
      delivery_date: load.delivery_date || '',
      delivery_time: load.delivery_time || '',
      commodity: load.commodity || '',
      weight: load.weight || 0,
      temperature: load.temperature || '',
      rate_to_shipper: load.rate_to_shipper || 0,
      rate_to_carrier: load.rate_to_carrier || 0,
      fuel_surcharge: load.fuel_surcharge || 0,
      accessorials: load.accessorials || 0,
      special_instructions: load.special_instructions || '',
      cc_emails: (load.cc_emails || []).join(', ')
    });
    setEditingLoad(load);
    setShowForm(true);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const { load_number, ...updateData } = formData;
    const ccList = updateData.cc_emails ? updateData.cc_emails.split(',').map(e => e.trim()).filter(Boolean) : [];
    api.put(`/loads/${editingLoad.id}`, { ...updateData, cc_emails: ccList })
      .then(() => {
        toast.success('Load updated successfully!');
        setShowForm(false);
        setEditingLoad(null);
        loadData();
        resetForm();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to update load'));
  };

  const resetForm = () => {
    setFormData({
      load_number: '', shipper_id: '', carrier_id: '', driver_id: '',
      equipment_type: '', pickup_location: '', pickup_city: '', pickup_state: '',
      pickup_date: '', pickup_time: '', delivery_location: '', delivery_city: '',
      delivery_state: '', delivery_date: '', delivery_time: '', commodity: '',
      weight: 0, temperature: '', rate_to_shipper: 0, rate_to_carrier: 0,
      fuel_surcharge: 0, accessorials: 0, special_instructions: '', cc_emails: ''
    });
  };

  const downloadRateConfirmation = (loadId) => {
    api.get(`/pdf/rate-confirmation/${loadId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `rate_confirmation_${loadId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Rate Confirmation downloaded');
      })
      .catch(() => toast.error('Failed to download'));
  };

  const exportToExcel = () => {
    api.get('/export/loads', { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'loads.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Excel exported');
      })
      .catch(() => toast.error('Failed to export'));
  };

  const viewSpreadsheet = () => {
    // Create custom data with renamed columns
    const spreadsheetData = loads.map(load => {
      const broker = shippers.find(s => s.id === load.shipper_id);
      const carrier = carriers.find(c => c.id === load.carrier_id);
      const driver = drivers.find(d => d.id === load.driver_id);
      return {
        'Load #': load.load_number,
        'Broker': broker?.legal_name || 'N/A',
        'Carrier': carrier?.legal_name || 'N/A',
        'Driver': driver?.name || 'N/A',
        'Pickup City': load.pickup_city,
        'Pickup State': load.pickup_state,
        'Pickup Date': load.pickup_date,
        'Delivery City': load.delivery_city,
        'Delivery State': load.delivery_state,
        'Delivery Date': load.delivery_date,
        'Commodity': load.commodity,
        'Weight': load.weight,
        'Equipment': load.equipment_type,
        'Est. Miles': load.estimated_miles || 'N/A',
        'Broker Rate': `$${load.rate_to_shipper || 0}`,
        'Carrier Rate': `$${load.rate_to_carrier || 0}`,
        'Profit': `$${(load.rate_to_shipper || 0) - (load.rate_to_carrier || 0) - (load.fuel_surcharge || 0) - (load.accessorials || 0)}`,
        'Status': load.status
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(spreadsheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Loads');
    const htmlStr = XLSX.utils.sheet_to_html(ws);
    const newWindow = window.open('');
    newWindow.document.write(`
      <html>
        <head><title>Loads Spreadsheet</title>
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #4a5568; color: white; }
        </style>
        </head>
        <body>${htmlStr}</body>
      </html>
    `);
  };

  const handleDocumentUpload = async (loadId, documentType, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post(`/loads/${loadId}/upload-document?document_type=${documentType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`${documentType.toUpperCase()} uploaded successfully`);
      loadData(); // Refresh loads to get updated document list
    } catch (error) {
      toast.error(`Failed to upload ${documentType.toUpperCase()}`);
    } finally {
      setUploadingDoc(false);
    }
  };

  const downloadDocument = (loadId, documentType, filename) => {
    api.get(`/loads/${loadId}/documents/${documentType}/${filename}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Document downloaded');
      })
      .catch(() => toast.error('Failed to download document'));
  };

  const deleteDocument = (loadId, documentType, filename) => {
    if (!window.confirm(`Delete this ${documentType.toUpperCase()} document?`)) return;
    
    api.delete(`/loads/${loadId}/documents/${documentType}/${filename}`)
      .then(() => {
        toast.success('Document deleted');
        loadData();
      })
      .catch(() => toast.error('Failed to delete document'));
  };

  const openDocumentsDialog = (load) => {
    setSelectedLoadForDocs(load);
    setShowDocumentsDialog(true);
  };

  const createLoadFolder = (loadId, loadNumber) => {
    api.post(`/loads/${loadId}/create-folder`)
      .then(res => {
        if (res.data.already_existed) {
          toast.info(`Folder already exists for ${loadNumber}`);
        } else {
          toast.success(`Folder created for ${loadNumber}`);
        }
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to create folder'));
  };

  const emailFactoring = (load) => { openSendDocs(load); };
  const invoiceBroker = (load) => { openSendDocs(load); };

  const openSendDocs = (load) => {
    const carrier = carriers.find(c => c.id === load.carrier_id);
    const shipper = shippers.find(s => s.id === load.shipper_id);
    const hasFactoring = carrier?.factoring_email;
    setSendDocModal(load);
    setSendDocType(hasFactoring ? 'factoring' : 'invoice');
    setSendDocEmail(hasFactoring ? carrier.factoring_email : (shipper?.email || carrier?.email || ''));
  };

  const sendDocuments = () => {
    if (!sendDocEmail || !sendDocModal) { toast.error('Enter a recipient email'); return; }
    const payload = sendDocType === 'factoring'
      ? { factoring_email: sendDocEmail, send_type: 'factoring' }
      : { recipient_email: sendDocEmail, send_type: 'invoice' };
    api.post(`/loads/${sendDocModal.id}/email-factoring`, payload)
      .then(res => { toast.success(res.data.message); setSendDocModal(null); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to send'));
  };

  const saveCommission = () => {
    api.put('/org/commission-settings', {
      dispatch_fee_percent: parseFloat(commissionForm.dispatch_fee_percent),
      factoring_percent: parseFloat(commissionForm.factoring_percent)
    })
      .then(() => { toast.success('Commission settings updated!'); setShowCommission(false); loadData(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to save'));
  };

  const openEmailClient = (load, useGmail = false) => {
    const carrier = carriers.find(c => c.id === load.carrier_id);
    const shipper = shippers.find(s => s.id === load.shipper_id);
    
    const subject = `Rate Confirmation - Load ${load.load_number}`;
    const body = `Hello,

Please find the rate confirmation details for Load ${load.load_number}:

Pickup: ${load.pickup_city}, ${load.pickup_state} on ${load.pickup_date}
Delivery: ${load.delivery_city}, ${load.delivery_state} on ${load.delivery_date}
Commodity: ${load.commodity}
Weight: ${load.weight} lbs
Rate: $${load.rate_to_carrier}

To download the full Rate Confirmation PDF, please visit:
${window.location.origin}

Thank you!
Urbanvision Logistics LLC`;

    if (useGmail) {
      // Open Gmail web interface
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(carrier?.email || '')}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(gmailUrl, '_blank');
    } else {
      // Open default email client
      const mailtoLink = `mailto:${carrier?.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
    }
  };

  const togglePin = (loadId) => {
    api.put(`/loads/${loadId}/pin`).then(res => {
      setLoads(prev => prev.map(l => l.id === loadId ? { ...l, pinned: res.data.pinned } : l));
      toast.success(res.data.pinned ? 'Load pinned to top' : 'Load unpinned');
    }).catch(() => toast.error('Failed to pin load'));
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterCity('');
    setFilterCarrier('all');
    setFilterBroker('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const hasActiveFilters = filterStatus !== 'all' || filterCity || filterCarrier !== 'all' || filterBroker !== 'all' || filterDateFrom || filterDateTo;

  // Apply filters and sorting (pinned first)
  const filteredLoads = loads
    .filter(load => {
      if (filterStatus !== 'all' && load.status !== filterStatus) return false;
      if (filterCity) {
        const city = filterCity.toLowerCase();
        if (!load.pickup_city?.toLowerCase().includes(city) && !load.delivery_city?.toLowerCase().includes(city) && !load.pickup_state?.toLowerCase().includes(city) && !load.delivery_state?.toLowerCase().includes(city)) return false;
      }
      if (filterCarrier !== 'all' && load.carrier_id !== filterCarrier) return false;
      if (filterBroker !== 'all' && load.shipper_id !== filterBroker) return false;
      if (filterDateFrom && load.pickup_date < filterDateFrom) return false;
      if (filterDateTo && load.pickup_date > filterDateTo) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return 0;
    });

  return (
    <div className="space-y-6" data-testid="loads-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Loads</h1>
          <p className="text-slate-600 mt-1">
            Book and manage loads
            {hasActiveFilters && <span className="ml-2 text-blue-600">({filteredLoads.length} of {loads.length} shown)</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(user.role === 'super_admin' || user.role === 'broker') && (
            <Button variant="outline" onClick={() => setShowCommission(!showCommission)} data-testid="commission-btn" className="text-orange-600 border-orange-300">
              <Percent className="w-4 h-4 mr-2" />
              Commission {commission.dispatch_fee_percent}%
            </Button>
          )}
          <Button onClick={viewSpreadsheet} variant="outline" data-testid="view-spreadsheet-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            View Spreadsheet
          </Button>
          <Button onClick={exportToExcel} variant="outline" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Dialog open={showDriverForm} onOpenChange={setShowDriverForm}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-driver-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Driver
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Driver</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleDriverSubmit} className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input required value={driverFormData.name} onChange={e => setDriverFormData({...driverFormData, name: e.target.value})} />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input required type="tel" value={driverFormData.phone} onChange={e => setDriverFormData({...driverFormData, phone: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={driverFormData.email} onChange={e => setDriverFormData({...driverFormData, email: e.target.value})} />
                </div>
                <div>
                  <Label>Carrier *</Label>
                  <Select value={driverFormData.carrier_id} onValueChange={v => setDriverFormData({...driverFormData, carrier_id: v})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      {carriers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Truck Number</Label>
                  <Input value={driverFormData.truck_number} onChange={e => setDriverFormData({...driverFormData, truck_number: e.target.value})} />
                </div>
                <div>
                  <Label>Trailer Number</Label>
                  <Input value={driverFormData.trailer_number} onChange={e => setDriverFormData({...driverFormData, trailer_number: e.target.value})} />
                </div>
                <Button type="submit" className="w-full">Add Driver</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) { setEditingLoad(null); resetForm(); } }}>
            <DialogTrigger asChild>
              <Button data-testid="book-load-btn">
                <Plus className="w-4 h-4 mr-2" />
                Book Load
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingLoad ? `Edit Load — ${editingLoad.load_number}` : 'Book New Load'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={editingLoad ? handleUpdate : handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Broker *</Label>
                    <Select value={formData.shipper_id} onValueChange={v => setFormData({...formData, shipper_id: v})} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select broker" />
                      </SelectTrigger>
                      <SelectContent>
                        {shippers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.legal_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Carrier *</Label>
                    <Select value={formData.carrier_id} onValueChange={v => setFormData({...formData, carrier_id: v})} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select carrier" />
                      </SelectTrigger>
                      <SelectContent>
                        {carriers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Driver *</Label>
                    <Select value={formData.driver_id} onValueChange={v => setFormData({...formData, driver_id: v})} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select driver" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Equipment Type *</Label>
                    <Input required value={formData.equipment_type} onChange={e => setFormData({...formData, equipment_type: e.target.value})} />
                  </div>
                  
                  <div className="col-span-2"><h3 className="font-bold text-lg">Pickup Details</h3></div>
                  <div className="col-span-2">
                    <Label>Pickup Location *</Label>
                    <Input required value={formData.pickup_location} onChange={e => setFormData({...formData, pickup_location: e.target.value})} />
                  </div>
                  <div>
                    <Label>Pickup City *</Label>
                    <Input required value={formData.pickup_city} onChange={e => setFormData({...formData, pickup_city: e.target.value})} />
                  </div>
                  <div>
                    <Label>Pickup State *</Label>
                    <Input required value={formData.pickup_state} onChange={e => setFormData({...formData, pickup_state: e.target.value})} />
                  </div>
                  <div>
                    <Label>Pickup Date *</Label>
                    <Input required type="date" value={formData.pickup_date} onChange={e => setFormData({...formData, pickup_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Pickup Time *</Label>
                    <Input required type="time" value={formData.pickup_time} onChange={e => setFormData({...formData, pickup_time: e.target.value})} />
                  </div>
                  
                  <div className="col-span-2"><h3 className="font-bold text-lg">Delivery Details</h3></div>
                  <div className="col-span-2">
                    <Label>Delivery Location *</Label>
                    <Input required value={formData.delivery_location} onChange={e => setFormData({...formData, delivery_location: e.target.value})} />
                  </div>
                  <div>
                    <Label>Delivery City *</Label>
                    <Input required value={formData.delivery_city} onChange={e => setFormData({...formData, delivery_city: e.target.value})} />
                  </div>
                  <div>
                    <Label>Delivery State *</Label>
                    <Input required value={formData.delivery_state} onChange={e => setFormData({...formData, delivery_state: e.target.value})} />
                  </div>
                  <div>
                    <Label>Delivery Date *</Label>
                    <Input required type="date" value={formData.delivery_date} onChange={e => setFormData({...formData, delivery_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Delivery Time *</Label>
                    <Input required type="time" value={formData.delivery_time} onChange={e => setFormData({...formData, delivery_time: e.target.value})} />
                  </div>
                  
                  <div className="col-span-2"><h3 className="font-bold text-lg">Load Details</h3></div>
                  <div>
                    <Label>Commodity *</Label>
                    <Input required value={formData.commodity} onChange={e => setFormData({...formData, commodity: e.target.value})} />
                  </div>
                  <div>
                    <Label>Weight (lbs) *</Label>
                    <Input required type="number" value={formData.weight} onChange={e => setFormData({...formData, weight: parseInt(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Rate to Broker ($) *</Label>
                    <Input required type="number" step="0.01" value={formData.rate_to_shipper} onChange={e => setFormData({...formData, rate_to_shipper: parseFloat(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Rate to Carrier ($) *</Label>
                    <Input required type="number" step="0.01" value={formData.rate_to_carrier} onChange={e => setFormData({...formData, rate_to_carrier: parseFloat(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Fuel Surcharge ($)</Label>
                    <Input type="number" step="0.01" value={formData.fuel_surcharge} onChange={e => setFormData({...formData, fuel_surcharge: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Accessorials ($)</Label>
                    <Input type="number" step="0.01" value={formData.accessorials} onChange={e => setFormData({...formData, accessorials: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Special Instructions</Label>
                    <Input value={formData.special_instructions} onChange={e => setFormData({...formData, special_instructions: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>CC Emails (comma-separated)</Label>
                    <Input value={formData.cc_emails} onChange={e => setFormData({...formData, cc_emails: e.target.value})} placeholder="email1@example.com, email2@example.com" data-testid="form-cc-emails" />
                  </div>
                </div>
                <Button type="submit" className="w-full">{editingLoad ? 'Save Changes' : 'Book Load'}</Button>
              </form>
            </DialogContent>
          </Dialog>
          {user.role === 'super_admin' && (
            <Button variant="outline" onClick={() => {
              toast.info('Calculating mileage for all loads...');
              api.post('/loads/backfill-mileage').then(res => {
                toast.success(`Done! Updated ${res.data.updated} loads`);
                loadData();
              }).catch(() => toast.error('Failed'));
            }} data-testid="backfill-mileage-btn">
              <MapPin className="w-4 h-4 mr-2" />
              Calculate Mileage
            </Button>
          )}
        </div>
      </div>

      {/* Filter Bar + Auto-Refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant={showFilters ? "default" : "outline"} size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
          <Filter className="w-4 h-4 mr-1" />
          Filters {hasActiveFilters && `(${filteredLoads.length})`}
        </Button>
        <Button
          variant={autoRefresh ? "default" : "outline"}
          size="sm"
          onClick={() => { setAutoRefresh(!autoRefresh); if (!autoRefresh) { loadData(); toast.success('Auto-refresh ON (30s)'); } else { toast.info('Auto-refresh OFF'); } }}
          data-testid="auto-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
          {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh'}
        </Button>
        <Button variant="ghost" size="sm" onClick={loadData} data-testid="manual-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600">
            <X className="w-4 h-4 mr-1" /> Clear Filters
          </Button>
        )}
      </div>

      {showFilters && (
        <Card data-testid="filter-panel">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">City / State</Label>
                <Input placeholder="e.g. Dallas" value={filterCity} onChange={e => setFilterCity(e.target.value)} data-testid="filter-city" />
              </div>
              <div>
                <Label className="text-xs">Carrier</Label>
                <Select value={filterCarrier} onValueChange={setFilterCarrier}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Carriers</SelectItem>
                    {carriers.map(c => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Broker</Label>
                <Select value={filterBroker} onValueChange={setFilterBroker}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brokers</SelectItem>
                    {shippers.map(s => <SelectItem key={s.id} value={s.id}>{s.legal_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pickup From</Label>
                <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} data-testid="filter-date-from" />
              </div>
              <div>
                <Label className="text-xs">Pickup To</Label>
                <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} data-testid="filter-date-to" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredLoads.map(load => {
          const shipper = shippers.find(s => s.id === load.shipper_id);
          const carrier = carriers.find(c => c.id === load.carrier_id);
          const profit = load.rate_to_shipper - load.rate_to_carrier - load.fuel_surcharge - load.accessorials;
          
          return (
            <Card key={load.id} className={`card-hover ${load.pinned ? 'ring-2 ring-yellow-400 bg-yellow-50/50' : ''}`} data-testid={`load-card-${load.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{load.load_number}</CardTitle>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => togglePin(load.id)} title={load.pinned ? 'Unpin' : 'Pin to top'} data-testid={`pin-${load.id}`}>
                    <Pin className={`w-4 h-4 ${load.pinned ? 'text-yellow-600 fill-yellow-600' : 'text-slate-400'}`} />
                  </Button>
                </div>
                <p className="text-sm text-slate-500">
                  {load.pickup_city}, {load.pickup_state} → {load.delivery_city}, {load.delivery_state}
                  {load.estimated_miles && <span className="ml-2 font-semibold text-blue-600">({load.estimated_miles} mi)</span>}
                </p>
                {load.created_by_pin && <p className="text-xs text-blue-600 font-semibold">Agent: {load.created_by_pin}</p>}
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm"><strong>Broker:</strong> {shipper?.legal_name}</p>
                <p className="text-sm"><strong>Carrier:</strong> {carrier?.legal_name}</p>
                <p className="text-sm"><strong>Commodity:</strong> {load.commodity}</p>
                <p className="text-sm"><strong>Weight:</strong> {load.weight} lbs</p>
                <p className="text-sm"><strong>Status:</strong> <span className="capitalize">{load.status.replace('_', ' ')}</span></p>
                <p className="text-sm"><strong>Profit:</strong> ${profit.toFixed(2)}</p>
                <p className="text-sm">
                  <strong>Documents:</strong> BOL ({load.bol_documents?.length || 0}) | POD ({load.pod_documents?.length || 0})
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="outline" className="text-blue-600 hover:text-blue-700 border-blue-300" onClick={() => startEditLoad(load)} data-testid={`edit-load-${load.id}`}>
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-amber-600 hover:text-amber-700 border-amber-300" onClick={() => createLoadFolder(load.id, load.load_number)} data-testid={`create-folder-${load.id}`}>
                    <FolderPlus className="w-3 h-3 mr-1" />
                    Load Folder
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openDocumentsDialog(load)} data-testid={`manage-docs-${load.id}`}>
                    <FileText className="w-3 h-3 mr-1" />
                    Documents
                  </Button>
                  <Button size="sm" variant="outline" className="text-purple-600 hover:text-purple-700 border-purple-300" onClick={() => openSendDocs(load)} data-testid={`send-docs-${load.id}`}>
                    <Mail className="w-3 h-3 mr-1" />
                    {(() => {
                      const c = carriers.find(cc => cc.id === load.carrier_id);
                      return c?.factoring_email ? 'Send to Factoring' : 'Send Invoice';
                    })()}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadRateConfirmation(load.id)} data-testid={`download-rate-${load.id}`}>
                    <Download className="w-3 h-3 mr-1" />
                    Rate Conf
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-green-600 hover:text-green-700 border-green-300"
                    onClick={() => {
                      api.post(`/loads/${load.id}/send-notifications`)
                        .then(res => {
                          toast.success('Notifications sent!', {
                            description: res.data.recipients?.join(', ')
                          });
                        })
                        .catch(err => toast.error(err.response?.data?.detail || 'Failed to send notifications'));
                    }}
                    data-testid={`send-notifications-${load.id}`}
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Notify
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEmailClient(load, true)} data-testid={`email-gmail-${load.id}`}>
                    <Mail className="w-3 h-3 mr-1" />
                    Gmail
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEmailClient(load, false)} data-testid={`email-default-${load.id}`}>
                    <Mail className="w-3 h-3 mr-1" />
                    Default
                  </Button>
                  {user.role === 'super_admin' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        if (window.confirm('Delete this load? Only master admin can delete.')) {
                          api.delete(`/loads/${load.id}`)
                            .then(() => {
                              toast.success('Load deleted');
                              loadData();
                            })
                            .catch(err => toast.error(err.response?.data?.detail || 'Failed to delete'));
                        }
                      }}
                      data-testid={`delete-load-${load.id}`}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedLoadForDocs && (
        <Dialog open={showDocumentsDialog} onOpenChange={setShowDocumentsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Documents for {selectedLoadForDocs.load_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* BOL Section */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">Bill of Lading (BOL)</h3>
                  <label htmlFor="bol-upload">
                    <Button size="sm" disabled={uploadingDoc} className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload BOL
                    </Button>
                    <input
                      id="bol-upload"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleDocumentUpload(selectedLoadForDocs.id, 'bol', file);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
                {selectedLoadForDocs.bol_documents && selectedLoadForDocs.bol_documents.length > 0 ? (
                  <div className="space-y-2">
                    {selectedLoadForDocs.bol_documents.map((docPath, idx) => {
                      const filename = docPath.split('/').pop();
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">{filename}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadDocument(selectedLoadForDocs.id, 'bol', filename)}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                            {(user.role === 'broker' || user.role === 'super_admin') && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600"
                                onClick={() => deleteDocument(selectedLoadForDocs.id, 'bol', filename)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No BOL documents uploaded</p>
                )}
              </div>

              {/* POD Section */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">Proof of Delivery (POD)</h3>
                  <label htmlFor="pod-upload">
                    <Button size="sm" disabled={uploadingDoc} className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload POD
                    </Button>
                    <input
                      id="pod-upload"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleDocumentUpload(selectedLoadForDocs.id, 'pod', file);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
                {selectedLoadForDocs.pod_documents && selectedLoadForDocs.pod_documents.length > 0 ? (
                  <div className="space-y-2">
                    {selectedLoadForDocs.pod_documents.map((docPath, idx) => {
                      const filename = docPath.split('/').pop();
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">{filename}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadDocument(selectedLoadForDocs.id, 'pod', filename)}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                            {(user.role === 'broker' || user.role === 'super_admin') && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600"
                                onClick={() => deleteDocument(selectedLoadForDocs.id, 'pod', filename)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No POD documents uploaded</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Commission Settings Panel */}
      {showCommission && (
        <Card className="border-orange-200 bg-orange-50/30" data-testid="commission-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Percent className="w-5 h-5 text-orange-600" />
              Commission Split Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">Set the dispatch fee % and factoring company %. After factoring, the remaining splits 50/50 between company and agent.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dispatch Fee %</Label>
                <Input type="number" step="0.1" min="0" max="100" value={commissionForm.dispatch_fee_percent} onChange={e => setCommissionForm({...commissionForm, dispatch_fee_percent: e.target.value})} data-testid="loads-fee-input" />
              </div>
              <div>
                <Label>Factoring %</Label>
                <Input type="number" step="0.1" min="0" max="100" value={commissionForm.factoring_percent} onChange={e => setCommissionForm({...commissionForm, factoring_percent: e.target.value})} data-testid="loads-factoring-input" />
              </div>
            </div>
            <div className="bg-white rounded p-2 text-xs space-y-1 border">
              <p><strong>On $2,000 load:</strong> Fee: ${(2000 * parseFloat(commissionForm.dispatch_fee_percent || 0) / 100).toFixed(2)} | Factoring: ${(2000 * parseFloat(commissionForm.factoring_percent || 0) / 100).toFixed(2)} | Company: ${((2000 * (parseFloat(commissionForm.dispatch_fee_percent || 0) - parseFloat(commissionForm.factoring_percent || 0)) / 100) / 2).toFixed(2)} | Agent: ${((2000 * (parseFloat(commissionForm.dispatch_fee_percent || 0) - parseFloat(commissionForm.factoring_percent || 0)) / 100) / 2).toFixed(2)}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveCommission} data-testid="save-loads-commission">Save</Button>
              <Button size="sm" variant="outline" onClick={() => setShowCommission(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send Documents Modal */}
      <Dialog open={!!sendDocModal} onOpenChange={() => setSendDocModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Send Documents — {sendDocModal?.load_number}
            </DialogTitle>
          </DialogHeader>
          {sendDocModal && (
            <div className="space-y-4">
              {(() => {
                const carrier = carriers.find(c => c.id === sendDocModal.carrier_id);
                const shipper = shippers.find(s => s.id === sendDocModal.shipper_id);
                const hasFactoring = carrier?.factoring_email;
                return (
                  <>
                    {!hasFactoring && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        <strong>No factoring company</strong> on file for {carrier?.legal_name || 'this carrier'}. You can send the invoice directly to the broker or carrier instead.
                      </div>
                    )}
                    <div>
                      <Label>Send as:</Label>
                      <div className="flex gap-2 mt-1">
                        {hasFactoring && (
                          <Button size="sm" variant={sendDocType === 'factoring' ? 'default' : 'outline'} onClick={() => { setSendDocType('factoring'); setSendDocEmail(carrier?.factoring_email || ''); }} data-testid="send-type-factoring">
                            Factoring Company
                          </Button>
                        )}
                        <Button size="sm" variant={sendDocType === 'invoice' && sendDocEmail === (shipper?.email || '') ? 'default' : 'outline'} onClick={() => { setSendDocType('invoice'); setSendDocEmail(shipper?.email || ''); }} data-testid="send-type-broker">
                          Invoice Broker
                        </Button>
                        <Button size="sm" variant={sendDocType === 'invoice' && sendDocEmail === (carrier?.email || '') ? 'default' : 'outline'} onClick={() => { setSendDocType('invoice'); setSendDocEmail(carrier?.email || ''); }} data-testid="send-type-carrier">
                          Invoice Carrier
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Recipient Email</Label>
                      <Input value={sendDocEmail} onChange={e => setSendDocEmail(e.target.value)} placeholder="email@example.com" data-testid="send-doc-email" />
                    </div>
                    <div className="bg-slate-50 rounded p-2 text-xs text-slate-600">
                      <p><strong>Load:</strong> {sendDocModal.load_number} | <strong>Amount:</strong> ${sendDocModal.rate_to_shipper}</p>
                      <p><strong>Carrier:</strong> {carrier?.legal_name || 'N/A'} | <strong>Broker:</strong> {shipper?.legal_name || 'N/A'}</p>
                      {hasFactoring && <p><strong>Factoring:</strong> {carrier.factoring_company} ({carrier.factoring_email})</p>}
                    </div>
                    <Button className="w-full" onClick={sendDocuments} data-testid="confirm-send-docs">
                      <Send className="w-4 h-4 mr-2" />
                      {sendDocType === 'factoring' ? 'Send POD to Factoring' : 'Send Invoice'}
                    </Button>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
